/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Client as TwilioClient, Conversation, Message, Participant } from '@twilio/conversations';
import axios from 'axios';
import { CacheManager as GlobalCacheManager } from '../utils/CacheManager';
import RequestManager from '../utils/RequestManager';
import { dedupeConversationsBySid } from '../utils/dedupeConversationsBySid';
import { useMessagingConfig, MessagingConfig } from './MessagingConfigContext';
import { configService } from '../services/configService';

// Constantes
const THROTTLE = {
  TOKEN_REFRESH: 3600000,      // 1 heure entre les refreshs de token
  WEBSOCKET_RECONNECT: 10000,  // 10 secondes entre tentatives de reconnexion
};

// Twilio event types
// Ces noms exacts correspondent aux événements Twilio mais nous devons les traiter avec le cast as any
const TWILIO_EVENTS = {
  EVENT_MESSAGE_ADDED: 'messageAdded',
  EVENT_MESSAGE_UPDATED: 'messageUpdated',
  EVENT_TYPING_STARTED: 'typingStarted',
  EVENT_TYPING_ENDED: 'typingEnded',
  EVENT_PARTICIPANT_UPDATED: 'participantUpdated',
  EVENT_CONVERSATION_UPDATED: 'conversationUpdated',
  EVENT_CONNECTION_STATE_CHANGED: 'connectionStateChanged'
};

// API endpoints
// API endpoints using configService for correct routing (Split API)
const API_ENDPOINTS = {
  get CONVERSATIONS() { return configService.getFullApiUrl('/messaging/conversations'); },
  get MESSAGES() { return configService.getFullApiUrl('/messaging/conversations'); },
  get TOKEN() { return configService.getFullApiUrl('/messaging/token'); },
  get SEND() { return configService.getFullApiUrl('/messaging/send'); },
  get NOTIFY_MESSAGE() { return configService.getFullApiUrl('/messaging/notify-message'); },
  get CREATE() { return configService.getFullApiUrl('/messaging/create'); },
  get USERS() { return configService.getFullApiUrl('/users'); },
  get PROS() { return configService.getFullApiUrl('/pros'); },
  get CLIENTS() { return configService.getFullApiUrl('/clients'); },
};

async function triggerInboundMessageNotifications(
  notifyUrl: string,
  conversationSid: string,
  senderIdentity: string,
  messageSid: string,
  messageBody: string,
  mediaUrl?: string
): Promise<void> {
  try {
    await axios.post(notifyUrl, {
      conversation_sid: conversationSid,
      sender_identity: senderIdentity,
      message_sid: messageSid,
      message_body: messageBody,
      media_url: mediaUrl,
      message_date_created: new Date().toISOString(),
    });
  } catch {
    // Secours si webhook Twilio absent ; ne pas bloquer l'UI
  }
}

/** Twilio CDS peut timeouter ponctuellement : quelques tentatives avant d'échouer. */
async function withTwilioSubscriptionRetry<T>(fn: () => Promise<T>): Promise<T> {
  const maxAttempts = 3;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (e: unknown) {
      lastError = e;
      const msg = (e instanceof Error ? e.message : String(e)).toLowerCase();
      const transient =
        msg.includes('timed out') ||
        msg.includes('timeout') ||
        msg.includes('status: 0') ||
        msg.includes('network') ||
        msg.includes('failed to fetch');
      if (!transient || attempt === maxAttempts) {
        break;
      }
      await new Promise((r) => setTimeout(r, 1500 * attempt));
    }
  }
  throw lastError;
}

// Renommer l'interface locale pour éviter conflit
export interface AppConversation {
  conversation_sid: string;
  friendly_name?: string;
  last_message?: string;
  last_message_author?: string;
  participant_identity: string;
  participant_avatar?: string;
  slug?: string;
  hasUnreadMessages?: boolean;
  last_message_time?: string;
  date_created?: string;
  typing?: boolean;
  unreadMessagesCount?: number;
}

export interface AppMessage {
  message_sid?: string;
  conversation_sid: string;
  message_body?: string;
  media_url?: string;
  timestamp: string;
  sender_identity: string;
  author?: string;
  author_name?: string;
  body?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  retry?: () => void;
  attributes?: any;
  temp_id?: string; // ID temporaire pour tracking des messages optimistes
}

interface UserCacheData {
  profile_photo_url?: string;
  name?: string;
  lastname?: string;
}

interface ProCacheData {
  profile_photo_url?: string;
  user_profile_photo_url?: string;
  name?: string;
}

export interface TwilioConversationsContextType {
  conversations: AppConversation[];
  messages: { [sid: string]: AppMessage[] };
  typing: { [sid: string]: boolean };
  loading: boolean;
  error: string | null;
  twilioStatus: 'connecting' | 'connected' | 'failed' | 'denied';
  fetchMessages: (conversationSid: string) => Promise<AppMessage[]>;
  sendMessage: (conversationSid: string, body: string, mediaUrl?: string, tempId?: string) => Promise<{ success: boolean; message?: AppMessage; error?: string }>;
  sendTypingIndicator: (conversationSid: string) => void;
  createConversation: (
    participantIdentity: string,
    firstMessage: string,
    participantDisplayName?: string
  ) => Promise<{ success: boolean; conversationSid?: string; error?: string }>;
  markConversationAsRead: (conversationSid: string) => Promise<void>;
  reconnect: () => void;
  refreshUnreadMessagesCount: () => void;
}

// Avatar par défaut
const DEFAULT_AVATAR = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';

// Déclaration de la propriété globale pour suivre la conversation active
declare global {
  interface Window {
    __twilio_selected_conversation?: string;
  }
}

// Context
export const TwilioConversationsContext = createContext<TwilioConversationsContextType | undefined>(undefined);


// Cache pour les avatars et noms
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<T> {
  value: T;
  expires: number;
}

const createTimedCache = <T,>() => {
  const store = new Map<string, CacheEntry<T>>();

  return {
    get(key: string): T | undefined {
      const entry = store.get(key);
      if (!entry) return undefined;
      if (Date.now() > entry.expires) {
        store.delete(key);
        return undefined;
      }
      return entry.value;
    },
    set(key: string, value: T, ttl: number = CACHE_TTL_MS) {
      store.set(key, { value, expires: Date.now() + ttl });
    },
    has(key: string): boolean {
      const entry = store.get(key);
      if (!entry) return false;
      if (Date.now() > entry.expires) {
        store.delete(key);
        return false;
      }
      return true;
    },
    clearExpired() {
      store.forEach((entry, key) => {
        if (Date.now() > entry.expires) {
          store.delete(key);
        }
      });
    }
  };
};

const userCache = createTimedCache<UserCacheData>();
const proCache = createTimedCache<ProCacheData>();

// Cache persistant pour les conversations (localStorage)
const CONVERSATIONS_CACHE_KEY = 'twilio_conversations_cache';
const MESSAGES_CACHE_KEY = 'twilio_messages_cache';
const CONVERSATIONS_CACHE_VERSION = 1;
const MESSAGES_CACHE_VERSION = 1;
const CONVERSATIONS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MESSAGES_CACHE_TTL = 10 * 60 * 1000; // 10 minutes pour les messages

interface ConversationsCache {
  version: number;
  timestamp: number;
  identity: string;
  conversations: AppConversation[];
}

interface MessagesCache {
  version: number;
  timestamp: number;
  identity: string;
  messages: { [sid: string]: AppMessage[] };
}

const saveConversationsToCache = (identity: string, conversations: AppConversation[]) => {
  try {
    const cache: ConversationsCache = {
      version: CONVERSATIONS_CACHE_VERSION,
      timestamp: Date.now(),
      identity,
      conversations: dedupeConversationsBySid(conversations),
    };
    localStorage.setItem(CONVERSATIONS_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Ignorer les erreurs de localStorage (quota, etc.)
  }
};

const loadConversationsFromCache = (identity: string): AppConversation[] | null => {
  try {
    const cached = localStorage.getItem(CONVERSATIONS_CACHE_KEY);
    if (!cached) return null;

    const cache: ConversationsCache = JSON.parse(cached);

    // Vérifier la version, l'identité et la validité du cache
    if (
      cache.version !== CONVERSATIONS_CACHE_VERSION ||
      cache.identity !== identity ||
      Date.now() - cache.timestamp > CONVERSATIONS_CACHE_TTL
    ) {
      localStorage.removeItem(CONVERSATIONS_CACHE_KEY);
      return null;
    }

    return dedupeConversationsBySid(cache.conversations || []);
  } catch (e) {
    return null;
  }
};

const saveMessagesToCache = (identity: string, messages: { [sid: string]: AppMessage[] }) => {
  try {
    const cache: MessagesCache = {
      version: MESSAGES_CACHE_VERSION,
      timestamp: Date.now(),
      identity,
      messages
    };
    localStorage.setItem(MESSAGES_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    // Ignorer les erreurs de localStorage (quota, etc.)
  }
};

const loadMessagesFromCache = (identity: string): { [sid: string]: AppMessage[] } | null => {
  try {
    const cached = localStorage.getItem(MESSAGES_CACHE_KEY);
    if (!cached) return null;

    const cache: MessagesCache = JSON.parse(cached);

    // Vérifier la version, l'identité et la validité du cache
    if (
      cache.version !== MESSAGES_CACHE_VERSION ||
      cache.identity !== identity ||
      Date.now() - cache.timestamp > MESSAGES_CACHE_TTL
    ) {
      localStorage.removeItem(MESSAGES_CACHE_KEY);
      return null;
    }

    return cache.messages;
  } catch (e) {
    return null;
  }
};

// 🔧 CORRECTION : Éviter la double souscription aux événements
const subscribedConversations = new Set<string>();

// Déclaration anticipée pour éviter l'erreur de référence circulaire
const subscribeToConversationRef = { current: (conv: Conversation) => { } };

export const TwilioConversationsProvider: React.FC<{ identity: string; children: React.ReactNode }> = ({ identity, children }) => {
  const config = useMessagingConfig();
  const [client, setClient] = useState<TwilioClient | null>(null);
  // Charger les conversations depuis le cache immédiatement pour éviter la friction
  const [conversations, setConversations] = useState<AppConversation[]>(() => {
    const cached = loadConversationsFromCache(identity);
    return cached || [];
  });
  // Charger les messages depuis le cache immédiatement
  const [messages, setMessages] = useState<{ [sid: string]: AppMessage[] }>(() => {
    const cached = loadMessagesFromCache(identity);
    return cached || {};
  });
  const [typing, setTyping] = useState<{ [sid: string]: boolean }>({});
  // Ne pas bloquer l'UI si on a des conversations en cache
  const [loading, setLoading] = useState(() => {
    const cached = loadConversationsFromCache(identity);
    return !cached; // Si on a du cache, on n'affiche pas le loading
  });
  const [error, setError] = useState<string | null>(null);
  const [twilioStatus, setTwilioStatus] = useState<'connecting' | 'connected' | 'failed' | 'denied'>('connecting');
  const reconnectRef = useRef<() => void>();

  // Refs pour stabiliser les callbacks dépendant de l'état
  const conversationsRef = useRef<AppConversation[]>(conversations);
  const messagesRef = useRef<{ [sid: string]: AppMessage[] }>(messages);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const formatPersonDisplayName = (name?: string, lastname?: string): string => {
    return `${(name || '').trim()} ${(lastname || '').trim()}`.trim();
  };

  // Récupère les informations d'un utilisateur (pour avatar et nom)
  const fetchUserInfo = useCallback(async (userId: string): Promise<UserCacheData> => {
    const cachedUser = userCache.get(userId);
    if (cachedUser) {
      return cachedUser;
    }

    try {
      const cleanUserId = userId.replace('user_', '');
      const response = await axios.get(`${API_ENDPOINTS.USERS}/${cleanUserId}`);
      const userData: UserCacheData = {
        profile_photo_url: response.data.profile_photo_url,
        name: response.data.name,
        lastname: response.data.lastname
      };
      userCache.set(userId, userData);
      return userData;
    } catch (e) {
      return {};
    }
  }, []);

  // Client invité (identité invited_{client_id}, sans compte user)
  const fetchInvitedClientInfo = useCallback(async (invitedIdentity: string): Promise<UserCacheData> => {
    const cached = userCache.get(invitedIdentity);
    if (cached) {
      return cached;
    }

    try {
      const clientId = invitedIdentity.replace('invited_', '');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.CLIENTS}/${clientId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const client = response.data?.client;
      const clientData: UserCacheData = {
        name: client?.name,
        lastname: client?.lastname,
        profile_photo_url: client?.profile_photo_url,
      };
      userCache.set(invitedIdentity, clientData);
      return clientData;
    } catch {
      return {};
    }
  }, []);

  // Récupère les informations d'un formateur (pour avatar et nom)
  const fetchProInfo = useCallback(async (proId: string): Promise<ProCacheData> => {
    const cachedPro = proCache.get(proId);
    if (cachedPro) {
      return cachedPro;
    }

    try {
      const cleanProId = proId.replace('pro_', '');
      const response = await axios.get(`${API_ENDPOINTS.PROS}/${cleanProId}`);
      const proData: ProCacheData = {
        profile_photo_url: response.data.profile_photo_url,
        user_profile_photo_url: response.data.user_profile_photo_url,
        name: response.data.name
      };
      proCache.set(proId, proData);
      return proData;
    } catch (e) {
      return {};
    }
  }, []);

  const resolveParticipantDisplayName = useCallback(
    async (participantIdentity: string): Promise<string | undefined> => {
      if (participantIdentity.startsWith('user_')) {
        const userData = await fetchUserInfo(participantIdentity);
        const full = formatPersonDisplayName(userData.name, userData.lastname);
        return full || undefined;
      }
      if (participantIdentity.startsWith('invited_')) {
        const clientData = await fetchInvitedClientInfo(participantIdentity);
        const full = formatPersonDisplayName(clientData.name, clientData.lastname);
        return full || undefined;
      }
      if (participantIdentity.startsWith('pro_')) {
        const proData = await fetchProInfo(participantIdentity);
        return (proData.name || '').trim() || undefined;
      }
      return undefined;
    },
    [fetchUserInfo, fetchInvitedClientInfo, fetchProInfo]
  );

  // Récupère le token Twilio via l'API
  const fetchTwilioToken = useCallback(async (identity: string) => {
    try {
      const response = await axios.get(API_ENDPOINTS.TOKEN, { params: { identity } });
      const token = response.data?.token;
      if (!token || typeof token !== 'string') {
        throw new Error('Réponse serveur invalide : token Twilio manquant');
      }
      return token;
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      const serverMsg = ax.response?.data?.message;
      if (typeof serverMsg === 'string' && serverMsg.trim()) {
        throw new Error(serverMsg);
      }
      if (ax.message) throw new Error(ax.message);
      throw new Error('Erreur lors de la récupération du token Twilio');
    }
  }, []);

  // Charge les détails des participants pour une conversation
  const loadParticipantDetails = useCallback(async (conv: Conversation) => {
    try {
      const participants = await conv.getParticipants();
      let otherParticipant = participants.find(p => p.identity !== identity);

      if (!otherParticipant || !otherParticipant.identity) return { identity: '', avatar: DEFAULT_AVATAR, name: 'Conversation' };

      let avatar = DEFAULT_AVATAR;
      let name = '';

      if (otherParticipant.identity.startsWith('user_')) {
        const userData = await fetchUserInfo(otherParticipant.identity);
        avatar = userData.profile_photo_url || DEFAULT_AVATAR;
        name = formatPersonDisplayName(userData.name, userData.lastname) || 'Client';
      } else if (otherParticipant.identity.startsWith('invited_')) {
        const clientData = await fetchInvitedClientInfo(otherParticipant.identity);
        avatar = clientData.profile_photo_url || DEFAULT_AVATAR;
        name = formatPersonDisplayName(clientData.name, clientData.lastname) || 'Client';
      } else if (otherParticipant.identity.startsWith('pro_')) {
        const proData = await fetchProInfo(otherParticipant.identity);
        avatar = proData.user_profile_photo_url || proData.profile_photo_url || DEFAULT_AVATAR;
        name = proData.name || 'Formateur';
      }

      return {
        identity: otherParticipant.identity,
        avatar,
        name
      };
    } catch (e) {
      return { identity: '', avatar: DEFAULT_AVATAR, name: 'Conversation' };
    }
  }, [identity, fetchUserInfo, fetchInvitedClientInfo, fetchProInfo]);

  const getUnreadMessagesCountSafe = useCallback(async (conv: Conversation): Promise<number> => {
    if (typeof conv.getUnreadMessagesCount !== 'function') return 0;
    try {
      const unread = await conv.getUnreadMessagesCount();
      return unread || 0;
    } catch {
      return 0;
    }
  }, []);

  interface LastMessageMetadata {
    text: string;
    time: string;
    author: string;
  }

  const getLastMessageMetadata = useCallback(async (conv: Conversation): Promise<LastMessageMetadata> => {
    try {
      const msgs = await conv.getMessages(1);
      if (msgs.items.length > 0) {
        return {
          text: msgs.items[0].body || '',
          time: msgs.items[0].dateCreated?.toISOString() || '',
          author: msgs.items[0].author || ''
        };
      }
    } catch {
      // Ignorer les erreurs
    }
    return {
      text: '',
      time: conv.dateUpdated?.toISOString() || '',
      author: ''
    };
  }, []);

  // Map Twilio Message -> AppMessage (défini avant initTwilioClient pour être utilisé dans le préchargement)
  const mapTwilioMessage = useCallback((msg: Message, conversation_sid: string): AppMessage => {
    let media_url = undefined;
    // Cast attributes en any pour accéder à media si présent
    let attrs: any = null;

    try {
      attrs = typeof msg.attributes === 'string'
        ? JSON.parse(msg.attributes || '{}')
        : msg.attributes || {};
    } catch (e) {
      attrs = {};
    }

    if (attrs && typeof attrs === 'object' && attrs.media && Array.isArray(attrs.media)) {
      media_url = attrs.media[0]?.url;
    }

    return {
      message_sid: msg.sid || undefined,
      conversation_sid,
      message_body: msg.body || undefined,
      media_url,
      timestamp: msg.dateCreated?.toISOString() || new Date().toISOString(),
      sender_identity: msg.author || '',
      author: msg.author || undefined,
      status: 'sent', // On ne peut pas savoir 'read' sans polling, donc 'sent' par défaut
      attributes: attrs
    };
  }, []);

  /** Hydrate une conversation Twilio (participant, non-lus, dernier message) et souscrit aux événements. */
  const hydrateConversation = useCallback(
    async (conv: Conversation): Promise<AppConversation | null> => {
      subscribeToConversationRef.current(conv);

      const [participantResult, unreadResult, lastMessageResult] = await Promise.allSettled([
        loadParticipantDetails(conv),
        getUnreadMessagesCountSafe(conv),
        getLastMessageMetadata(conv),
      ]);

      if (participantResult.status !== 'fulfilled') {
        return null;
      }

      const participant = participantResult.value;
      const unreadMsgCount = unreadResult.status === 'fulfilled' ? unreadResult.value : 0;
      const lastMessageMeta =
        lastMessageResult.status === 'fulfilled'
          ? lastMessageResult.value
          : { text: '', time: conv.dateUpdated?.toISOString() || '', author: '' };

      return {
        conversation_sid: conv.sid || '',
        friendly_name: participant.name || conv.friendlyName || undefined,
        participant_identity: participant.identity || '',
        participant_avatar: participant.avatar,
        last_message: lastMessageMeta.text,
        last_message_author: lastMessageMeta.author,
        hasUnreadMessages: unreadMsgCount > 0,
        last_message_time: lastMessageMeta.time || conv.dateUpdated?.toISOString() || '',
        date_created: conv.dateCreated?.toISOString() || '',
        typing: false,
        unreadMessagesCount: unreadMsgCount,
      } as AppConversation;
    },
    [loadParticipantDetails, getUnreadMessagesCountSafe, getLastMessageMetadata]
  );

  // Initialisation du client Twilio
  const initTwilioClient = useCallback(async () => {
    if (!identity?.trim()) {
      return;
    }
    setLoading(true);
    setTwilioStatus('connecting');
    setError(null);
    // 🔧 CORRECTION : S'assurer qu'on démarre avec un état propre
    subscribedConversations.clear();
    try {
      const token = await fetchTwilioToken(identity);
      const twilioClient = new TwilioClient(token);
      setClient(twilioClient);
      setTwilioStatus('connecting');

      twilioClient.on('connectionStateChanged', (state) => {
        if (state === 'connected') setTwilioStatus('connected');
        else if (state === 'denied') setTwilioStatus('denied');
        else if (state === 'disconnected' || state === 'error') setTwilioStatus('failed');
        else setTwilioStatus('connecting');
      });

      // Gestion reconnexion automatique
      reconnectRef.current = () => {
        twilioClient.shutdown();
        setClient(null);
        // 🔧 CORRECTION : Nettoyer les souscriptions lors de la reconnexion
        subscribedConversations.clear();
        setTimeout(() => {
          initTwilioClient();
        }, 1000);
      };

      const upsertConversationInState = async (conv: Conversation) => {
        try {
          const hydrated = await hydrateConversation(conv);
          if (!hydrated?.conversation_sid) return;
          setConversations((prev) => {
            const others = prev.filter((c) => c.conversation_sid !== hydrated.conversation_sid);
            const merged = [hydrated, ...others].sort((a, b) => {
              const dateA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
              const dateB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
              return dateB - dateA;
            });
            saveConversationsToCache(identity, merged);
            return merged;
          });
        } catch {
          // Événement temps réel : ne pas casser le provider
        }
      };

      // Charger les conversations existantes (retry si timeout réseau / CDS)
      const convPaginator = await withTwilioSubscriptionRetry(() =>
        twilioClient.getSubscribedConversations()
      );
      const loadedResults = await Promise.all(convPaginator.items.map((conv) => hydrateConversation(conv)));
      const loadedConvs = dedupeConversationsBySid(
        loadedResults.filter((c): c is AppConversation => Boolean(c))
      );

      // Trier les conversations par date du dernier message (plus récent en haut)
      loadedConvs.sort((a, b) => {
        const dateA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
        const dateB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
        return dateB - dateA;
      });

      // Sauvegarder dans le cache persistant
      saveConversationsToCache(identity, loadedConvs);

      setConversations(loadedConvs);
      setLoading(false);
      setTwilioStatus('connected');

      // Nouvelle conversation / rejointe après le chargement initial (ex. client contacte depuis le profil)
      twilioClient.on('conversationAdded', upsertConversationInState);
      twilioClient.on('conversationJoined', upsertConversationInState);

      // Précharger les messages des 2 conversations les plus récentes en arrière-plan
      // pour qu'ils soient disponibles instantanément au clic
      const topConversations = loadedConvs.slice(0, 2);
      topConversations.forEach((conv) => {
        // Précharger en arrière-plan sans bloquer
        setTimeout(() => {
          twilioClient.getConversationBySid(conv.conversation_sid).then(async (convObj) => {
            try {
              subscribeToConversationRef.current(convObj);
              const msgs = await convObj.getMessages();
              const arr = msgs.items.map((msg) => mapTwilioMessage(msg, conv.conversation_sid));
              setMessages((prev) => ({ ...prev, [conv.conversation_sid]: arr }));
            } catch (e) {
              // Ignorer les erreurs silencieusement pour le préchargement
            }
          }).catch(() => {
            // Ignorer les erreurs silencieusement
          });
        }, 100); // Petit délai pour ne pas bloquer le rendu initial
      });
    } catch (e: any) {
      let msg = e.message || 'Erreur lors de la connexion Twilio';
      if (
        typeof msg === 'string' &&
        msg.includes("Cannot read properties of undefined (reading 'then')")
      ) {
        msg =
          'Connexion Twilio refusée (souvent : jeton invalide, mauvais Conversations Service SID, ou panne Twilio). Vérifiez les variables TWILIO_* côté API.';
      }
      setError(msg);
      setTwilioStatus('failed');
      setLoading(false);
    }
  }, [identity, fetchTwilioToken, hydrateConversation, mapTwilioMessage]);

  // Sauvegarder automatiquement les conversations dans le cache à chaque modification
  useEffect(() => {
    if (conversations.length > 0 && !loading) {
      saveConversationsToCache(identity, conversations);
    }
  }, [conversations, identity, loading]);

  // Sauvegarder automatiquement les messages dans le cache à chaque modification
  useEffect(() => {
    const messagesCount = Object.keys(messages).length;
    if (messagesCount > 0 && !loading) {
      saveMessagesToCache(identity, messages);
    }
  }, [messages, identity, loading]);

  // Initialisation au montage (identity vide = pas d'appel token / pas de client Twilio)
  useEffect(() => {
    if (!identity?.trim()) {
      setClient(null);
      setLoading(false);
      setTwilioStatus('connecting');
      setError(null);
      subscribedConversations.clear();
      return;
    }
    initTwilioClient();
    return () => {
      if (client) client.shutdown();
      subscribedConversations.clear();
    };
    // eslint-disable-next-line
  }, [identity]);

  // Méthode pour fetch les messages d'une conversation
  const fetchMessages = useCallback(async (conversationSid: string) => {
    if (!client) {
      // Si pas de client mais qu'on a des messages en cache, les retourner
      const cached = messages[conversationSid];
      return cached || [];
    }

    // Vérifier si on a déjà des messages en cache pour cette conversation
    // Si oui, on les retourne immédiatement et on met à jour en arrière-plan
    const cachedMessages = messagesRef.current[conversationSid];
    if (cachedMessages && cachedMessages.length > 0) {
      // Mettre à jour en arrière-plan sans bloquer l'UI
      client.getConversationBySid(conversationSid).then(async (conv) => {
        subscribeToConversationRef.current(conv);
        const msgs = await conv.getMessages();
        const arr = msgs.items.map((msg) => mapTwilioMessage(msg, conversationSid));
        setMessages((prev) => ({ ...prev, [conversationSid]: arr }));
      }).catch(() => {
        // Ignorer les erreurs silencieusement pour le refresh en arrière-plan
      });
      return cachedMessages;
    }

    // Sinon, charger normalement
    const conv = await client.getConversationBySid(conversationSid);
    subscribeToConversationRef.current(conv);
    const msgs = await conv.getMessages();
    const arr = msgs.items.map((msg) => mapTwilioMessage(msg, conversationSid));
    setMessages((prev) => ({ ...prev, [conversationSid]: arr }));
    return arr;
  }, [client, mapTwilioMessage]); // Suppression de messages des dépendances

  // Indicateur de frappe
  const sendTypingIndicator = useCallback((conversationSid: string) => {
    // Désactivé pour supprimer la fonctionnalité "En train d'écrire"
    // Fonction maintenue pour compatibilité du code
  }, []);

  // Méthode pour mettre à jour le compteur de messages non lus
  const refreshUnreadMessagesCount = useCallback(async () => {
    if (!client) {
      // Client Twilio non disponible - log supprimé
      return;
    }

    // Début du rafraîchissement des compteurs de messages non lus - log supprimé

    try {
      // Récupérer toutes les conversations abonnées
      const convPaginator = await client.getSubscribedConversations();
      const updatedConvs: AppConversation[] = [];

      // Récupération des conversations - log supprimé

      // Pour chaque conversation, mettre à jour le compteur de messages non lus
      for (const conv of convPaginator.items) {
        // Trouver la conversation dans notre état local via la ref pour la stabilité
        const existingConv = conversationsRef.current.find(c => c.conversation_sid === conv.sid);
        if (!existingConv) {
          try {
            const hydrated = await hydrateConversation(conv);
            if (hydrated) {
              updatedConvs.push(hydrated);
            }
          } catch {
            // Conversation Twilio pas encore hydratable côté UI
          }
          continue;
        }

        try {
          // Logique simplifiée : si la conversation est actuellement affichée/sélectionnée,
          // on marque automatiquement ses messages comme lus
          // @ts-ignore - Accès à la variable selectedConversation définie dans BackOfficeMessagingPage
          const selectedConvSid = window.__twilio_selected_conversation;

          if (selectedConvSid === conv.sid) {
            updatedConvs.push({
              ...existingConv,
              hasUnreadMessages: false,
              unreadMessagesCount: 0
            });

            // Marquer comme lu sur Twilio également
            try {
              await conv.setAllMessagesRead();
            } catch (e) {
              // Ignorer les erreurs silencieusement car c'est une opération en arrière-plan
            }
            continue;
          }

          // Pour les autres conversations, obtenir le nombre de messages non lus
          let unreadMsgCount = 0;
          if (typeof conv.getUnreadMessagesCount === 'function') {
            unreadMsgCount = (await conv.getUnreadMessagesCount()) || 0;

            // Si Twilio dit qu'il y a des messages non lus, vérifier si le dernier est de nous
            // pour éviter les fausses notifications (latence de synchronisation)
            if (unreadMsgCount > 0) {
              const recentMessages = await conv.getMessages(1);
              if (recentMessages.items.length > 0 && recentMessages.items[0].author === identity) {
                // Si le dernier message est de nous, on considère qu'il n'y a pas de nouveaux messages pour NOUS
                if (unreadMsgCount === 1) {
                  unreadMsgCount = 0;
                }
              }
            }
          }

          // Mettre à jour l'objet conversation
          updatedConvs.push({
            ...existingConv,
            hasUnreadMessages: unreadMsgCount > 0,
            unreadMessagesCount: unreadMsgCount
          });

        } catch (e) {
          // Erreur lors de la récupération du nombre de messages non lus - log supprimé
          // En cas d'erreur, conserver l'état actuel
          updatedConvs.push(existingConv);
        }
      }

      // Mettre à jour l'état des conversations
      if (updatedConvs.length > 0) {
        setConversations(prevConvs => {
          // Mise à jour des conversations avec les nouveaux compteurs - log supprimé

          // Créer un nouvel état en remplaçant les conversations mises à jour
          const newConvs = [...prevConvs];
          let hasChanges = false;

          for (const updatedConv of updatedConvs) {
            const index = newConvs.findIndex(c => c.conversation_sid === updatedConv.conversation_sid);
            if (index >= 0) {
              // Vérifier si une mise à jour est nécessaire
              const current = newConvs[index];
              if (current.hasUnreadMessages !== updatedConv.hasUnreadMessages ||
                current.unreadMessagesCount !== updatedConv.unreadMessagesCount) {
                // Mise à jour nécessaire
                newConvs[index] = updatedConv;
                hasChanges = true;
                // Conversation mise à jour - log supprimé
              }
            }
          }

          // Ne retourner un nouvel état que si des changements ont été apportés
          return hasChanges ? newConvs : prevConvs;
        });
      } else {
        // Aucune conversation à mettre à jour - log supprimé
      }

      // Fin du rafraîchissement des compteurs de messages non lus - log supprimé
    } catch (e) {
      // Erreur lors de la mise à jour des compteurs de messages non lus - log supprimé
    }
  }, [client, identity, hydrateConversation]); // Suppression de conversations des dépendances

  // S'abonner aux événements d'une conversation
  const subscribeToConversation = useCallback((conv: Conversation) => {
    // 🔧 CORRECTION : Éviter la double souscription
    if (subscribedConversations.has(conv.sid)) {
      return; // Déjà souscrit
    }
    subscribedConversations.add(conv.sid);

    // Événement messageAdded
    conv.on('messageAdded', async (msg: Message) => {
      // Nouveau message reçu - log supprimé

      // Vérifier si c'est un nouveau message (pas un message optimiste)
      setMessages((prev) => {
        const existingMessages = prev[conv.sid] ? [...prev[conv.sid]] : [];

        // Si le message a un ID temporaire, remplacer le message optimiste
        const twilioMsg = mapTwilioMessage(msg, conv.sid);
        const attributes: any = typeof msg.attributes === 'string'
          ? JSON.parse(msg.attributes || '{}')
          : msg.attributes || {};

        const tempId = attributes.temp_id;

        if (tempId) {
          // Remplacer le message temporaire
          const msgIndex = existingMessages.findIndex(m => m.temp_id === tempId);
          if (msgIndex >= 0) {
            existingMessages[msgIndex] = {
              ...twilioMsg,
              temp_id: tempId,
              status: 'sent'
            };
            return { ...prev, [conv.sid]: existingMessages };
          }
        }

        // 🔧 CORRECTION : Vérifier si le message existe déjà par message_sid pour éviter les doublons
        const messageExists = existingMessages.find(m => m.message_sid === twilioMsg.message_sid);
        if (messageExists) {
          // Message déjà présent, ne pas l'ajouter à nouveau
          return prev;
        }

        // Ajouter un nouveau message
        const newMessages = [...existingMessages, twilioMsg];
        return { ...prev, [conv.sid]: newMessages };
      });

      // Mettre à jour le dernier message et l'indicateur de non-lu dans la liste des conversations
      if (msg.author !== identity) {
        try {
          // Obtenir le nombre actuel de messages non lus
          let unreadCount = 1; // Par défaut, au moins ce message est non lu

          try {
            if (typeof conv.getUnreadMessagesCount === 'function') {
              unreadCount = await conv.getUnreadMessagesCount() || 1;
            }
          } catch (e) {
            // Impossible de récupérer le nombre exact de messages non lus - log supprimé
          }

          setConversations((prev) => {
            const updatedConvs = prev.map(c => {
              if (c.conversation_sid === conv.sid) {
                return {
                  ...c,
                  hasUnreadMessages: true,
                  unreadMessagesCount: unreadCount,
                  last_message: msg.body || '',
                  last_message_author: msg.author || '',
                  last_message_time: msg.dateCreated?.toISOString() || new Date().toISOString()
                };
              }
              return c;
            });

            // Trier les conversations pour que celle avec le dernier message apparaisse en haut
            return updatedConvs.sort((a, b) => {
              const dateA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
              const dateB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
              return dateB - dateA;
            });
          });

          // Forcer un rafraîchissement des compteurs pour s'assurer
          // que la sidebar est bien mise à jour
          setTimeout(() => {
            // Rafraîchissement des compteurs après nouveau message - log supprimé
            refreshUnreadMessagesCount();
          }, 300);
        } catch (e) {
          // Erreur lors de la mise à jour des indicateurs de messages non lus - log supprimé
        }
      }
    });

    // Note: Les écouteurs d'événements pour les indicateurs de frappe ont été supprimés
    // pour désactiver la fonctionnalité "En train d'écrire"
  }, [identity, refreshUnreadMessagesCount]);

  // Assigner la fonction à la référence pour éviter la dépendance circulaire
  subscribeToConversationRef.current = subscribeToConversation;

  // Marquer une conversation comme lue
  const markConversationAsRead = useCallback(async (conversationSid: string) => {
    if (!client) return;

    // Marquage de la conversation comme lue - log supprimé

    try {
      // 1. Marquer tous les messages comme lus via l'API Twilio
      const conv = await client.getConversationBySid(conversationSid);
      await conv.setAllMessagesRead();

      // 2. Mettre à jour immédiatement cette conversation spécifique dans notre état local
      setConversations((prev) => {
        // Créer un nouvel état des conversations avec cette conversation mise à jour
        const updatedConvs = prev.map(c =>
          c.conversation_sid === conversationSid
            ? {
              ...c,
              hasUnreadMessages: false,
              unreadMessagesCount: 0
            }
            : c
        );

        // État des conversations mis à jour après lecture - log supprimé
        return updatedConvs;
      });

      // 3. Après un court délai pour laisser Twilio traiter le changement,
      // forcer une actualisation basée sur l'API pour s'assurer de la cohérence
      const refreshDelayMs = 200;
      setTimeout(() => {
        // Rafraîchissement forcé des compteurs - log supprimé
        refreshUnreadMessagesCount();
      }, refreshDelayMs);

      // 4. Et une deuxième actualisation plus tard pour s'assurer que les changements ont bien été propagés
      setTimeout(() => {
        // Deuxième rafraîchissement des compteurs - log supprimé
        refreshUnreadMessagesCount();
      }, 1000);

    } catch (error) {
      // Erreur lors du marquage des messages comme lus - log supprimé
      // En cas d'erreur, actualiser quand même les compteurs
      refreshUnreadMessagesCount();
    }
  }, [client, refreshUnreadMessagesCount]);

  // Méthode pour envoyer un message
  const sendMessage = useCallback(async (conversationSid: string, body: string, mediaUrl?: string | undefined, tempId?: string) => {
    if (!client) return { success: false, error: 'Client Twilio non initialisé' };

    // Ajouter le message optimiste au state si un ID temporaire est fourni
    if (tempId) {
      const optimisticMsg: AppMessage = {
        conversation_sid: conversationSid,
        message_body: body,
        media_url: mediaUrl,
        timestamp: new Date().toISOString(),
        sender_identity: identity,
        author: identity,
        status: 'sending',
        temp_id: tempId
      };

      setMessages((prev) => {
        const existingMessages = prev[conversationSid] ? [...prev[conversationSid]] : [];
        return { ...prev, [conversationSid]: [...existingMessages, optimisticMsg] };
      });

      // Mettre à jour la dernière date de message pour cette conversation
      setConversations((prevConvs) => {
        const updatedConvs = prevConvs.map(c => {
          if (c.conversation_sid === conversationSid) {
            return {
              ...c,
              last_message: body,
              last_message_author: identity,
              last_message_time: new Date().toISOString()
            };
          }
          return c;
        });

        // Trier les conversations pour que celle avec le message envoyé apparaisse en haut
        return updatedConvs.sort((a, b) => {
          const dateA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
          const dateB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
          return dateB - dateA;
        });
      });
    }

    try {
      const conv = await client.getConversationBySid(conversationSid);
      let msg: Message | undefined;
      // Typages améliorés pour attributes
      const messageAttributes: Record<string, any> = tempId ? { temp_id: tempId } : {};

      if (mediaUrl) {
        // Ajouter les attributs média
        messageAttributes.media = [{ url: mediaUrl, contentType: 'image/png' }];

        // Convertir en JSON string pour éviter les problèmes de typage
        const attributesJson = JSON.stringify(messageAttributes);

        const idx = await conv.sendMessage(body || ' ', attributesJson);
        const fetched = await conv.getMessages();
        msg = fetched.items.find(m => m.index === idx);
      } else {
        // Convertir en JSON string pour éviter les problèmes de typage
        const attributesJson = JSON.stringify(messageAttributes);

        const idx = await conv.sendMessage(body, attributesJson);
        const fetched = await conv.getMessages();
        msg = fetched.items.find(m => m.index === idx);
      }

      if (!msg) throw new Error('Message non trouvé après envoi');

      // Si un ID temporaire a été fourni, marquer le message comme envoyé
      if (tempId) {
        setMessages((prev) => {
          const existingMessages = prev[conversationSid] || [];
          const msgIndex = existingMessages.findIndex(m => m.temp_id === tempId);

          if (msgIndex >= 0) {
            const updatedMessages = [...existingMessages];
            updatedMessages[msgIndex] = {
              ...mapTwilioMessage(msg!, conversationSid),
              temp_id: tempId,
              status: 'sent'
            };
            return { ...prev, [conversationSid]: updatedMessages };
          }

          return prev;
        });
      }

      const sentMessage = mapTwilioMessage(msg, conversationSid);
      if (msg.sid) {
        void triggerInboundMessageNotifications(
          API_ENDPOINTS.NOTIFY_MESSAGE,
          conversationSid,
          identity,
          msg.sid,
          body,
          mediaUrl
        );
      }

      return { success: true, message: sentMessage };
    } catch (e: any) {
      // En cas d'erreur, mettre à jour le statut du message temporaire
      if (tempId) {
        setMessages((prev) => {
          const existingMessages = prev[conversationSid] || [];
          const msgIndex = existingMessages.findIndex(m => m.temp_id === tempId);

          if (msgIndex >= 0) {
            const updatedMessages = [...existingMessages];
            updatedMessages[msgIndex] = {
              ...updatedMessages[msgIndex],
              status: 'failed',
              retry: () => sendMessage(conversationSid, body, mediaUrl, tempId)
            };
            return { ...prev, [conversationSid]: updatedMessages };
          }

          return prev;
        });
      }

      return { success: false, error: e.message };
    }
  }, [client, identity, mapTwilioMessage]);

  // Créer une conversation
  const createConversation = useCallback(async (
    participantIdentity: string,
    firstMessage: string,
    participantDisplayName?: string
  ) => {
    try {
      const friendlyName =
        participantDisplayName?.trim() ||
        (await resolveParticipantDisplayName(participantIdentity));

      // Appel API pour créer la conversation côté serveur
      const response = await axios.post(API_ENDPOINTS.CREATE, {
        client_identity: participantIdentity,
        professional_identity: identity,
        friendly_name: friendlyName || undefined,
      });
      const conversationSid = response.data.conversation_sid;

      // Envoyer le premier message
      const tempId = `temp_${Date.now()}`;
      await sendMessage(conversationSid, firstMessage, undefined, tempId);

      // Récupérer les informations du participant
      let participantData: UserCacheData & ProCacheData = {};
      if (participantIdentity.startsWith('user_')) {
        participantData = await fetchUserInfo(participantIdentity);
      } else if (participantIdentity.startsWith('invited_')) {
        participantData = await fetchInvitedClientInfo(participantIdentity);
      } else if (participantIdentity.startsWith('pro_')) {
        participantData = await fetchProInfo(participantIdentity);
      }

      const displayName =
        friendlyName ||
        formatPersonDisplayName(participantData.name, participantData.lastname) ||
        (participantData as ProCacheData).name;

      // Ajouter la conversation à la liste
      const newConversation: AppConversation = {
        conversation_sid: conversationSid,
        friendly_name: displayName || 'Nouvelle conversation',
        participant_identity: participantIdentity,
        participant_avatar: participantData.profile_photo_url || participantData.user_profile_photo_url || DEFAULT_AVATAR,
        last_message: firstMessage,
        last_message_author: identity,
        hasUnreadMessages: false,
        last_message_time: new Date().toISOString(),
        date_created: new Date().toISOString(),
        typing: false
      };

      // Dédup par SID : l'API peut renvoyer une conv déjà listée + conversationJoined
      // peut aussi l'avoir upsertée → sans filtre, Olivier apparaît 2× (#409).
      setConversations((prev) =>
        dedupeConversationsBySid([newConversation, ...prev])
      );

      // Charger la conversation côté client
      try {
        await fetchMessages(conversationSid);
      } catch (err) {
        console.warn('⚠️ Erreur lors du fetch initial des messages (non bloquant):', err);
      }

      return { success: true, conversationSid };
    } catch (e: any) {
      console.error('🚨 Erreur createConversation:', e);
      return { success: false, error: e.message };
    }
  }, [identity, sendMessage, fetchMessages, fetchUserInfo, fetchInvitedClientInfo, fetchProInfo, resolveParticipantDisplayName]);

  // Reconnexion manuelle
  const reconnect = useCallback(() => {
    if (reconnectRef.current) reconnectRef.current();
  }, []);

  return (
    <TwilioConversationsContext.Provider
      value={{
        conversations,
        messages,
        typing,
        loading,
        error,
        twilioStatus,
        fetchMessages,
        sendMessage,
        sendTypingIndicator,
        createConversation,
        markConversationAsRead,
        reconnect,
        refreshUnreadMessagesCount,
      }}
    >
      {children}
    </TwilioConversationsContext.Provider>
  );
};

/** Identité Twilio `pro_{id}` — alignée sur ProtectedRoute (user.pro_id ou localStorage). */
export function resolveTwilioProIdentity(
  user?: { pro_id?: number | string | null } | null
): string {
  const fromUser = user?.pro_id;
  if (fromUser !== null && fromUser !== undefined && fromUser !== '') {
    const n = typeof fromUser === 'number' ? fromUser : Number(String(fromUser).trim());
    if (Number.isFinite(n) && n > 0) return `pro_${n}`;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('pro_id');
    if (stored) {
      const n = Number(stored.trim());
      if (Number.isFinite(n) && n > 0) return `pro_${n}`;
    }
  }
  return '';
}

export const EMPTY_TWILIO_CONVERSATIONS_CONTEXT: TwilioConversationsContextType = {
  conversations: [],
  messages: {},
  typing: {},
  loading: false,
  error: null,
  twilioStatus: 'connecting',
  fetchMessages: async () => [],
  sendMessage: async () => ({ success: false, error: 'Provider not available' }),
  sendTypingIndicator: () => {},
  createConversation: async () => ({ success: false, error: 'Provider not available' }),
  markConversationAsRead: async () => {},
  reconnect: () => {},
  refreshUnreadMessagesCount: () => {},
};

export const useTwilioConversations = () => {
  const ctx = useContext(TwilioConversationsContext);
  if (!ctx) throw new Error('useTwilioConversations doit être utilisé dans un TwilioConversationsProvider');
  return ctx;
};

/** Badges / menu : ne lève jamais si le provider n'est pas monté. */
export const useSafeTwilioConversations = (): TwilioConversationsContextType => {
  const ctx = useContext(TwilioConversationsContext);
  return ctx ?? EMPTY_TWILIO_CONVERSATIONS_CONTEXT;
};

/** Alias explicite (ticket #1332) — même comportement que useSafeTwilioConversations. */
export const useOptionalTwilioConversations = useSafeTwilioConversations;

export default TwilioConversationsContext;
